import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex } from '@mantine/core';
import { Select, TextInput } from 'react-hook-form-mantine';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { PermissionRole } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { ORGANIZATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
	useTranslationAdminDashboard,
	useTranslationCommonForm,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect } from 'react';

interface Props {
	permissions: {
		label: string;
		value: PermissionRole;
	}[];
	roleLabel: string;
	shouldResetForm: boolean;
	submitLabel?: string;
	submitAndAddAnotherLabel?: string;
	handleInviteMember: (
		firstName: string,
		lastName: string,
		email: string,
		role: PermissionRole,
		shouldCloseOnSuccess?: boolean
	) => void;
}

const DEFAULT_VALUES = {
	firstName: '',
	lastName: '',
	email: '',
	role: PermissionRole.VIEWER,
};

export const InviteTeamMemberForm = ({
	permissions,
	roleLabel,
	shouldResetForm,
	handleInviteMember,
	submitLabel,
	submitAndAddAnotherLabel,
}: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonForm = useTranslationCommonForm();
	const form = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: zodResolver(
			z.object({
				firstName: z
					.string({
						required_error: tCommonForm('field_required'),
					})
					.min(1, { message: tCommonForm('field_required') }),
				lastName: z
					.string({
						required_error: tCommonForm('field_required'),
					})
					.min(1, { message: tCommonForm('field_required') }),
				email: z
					.string({
						message: tCommonForm('field_required'),
					})
					.email({ message: tCommonForm('invalid_email') }),
				role: z.nativeEnum(PermissionRole),
			})
		),
		defaultValues: DEFAULT_VALUES,
	});

	useEffect(() => {
		if (shouldResetForm) {
			form.reset(DEFAULT_VALUES);
		}
	}, [shouldResetForm]);

	const { control } = form;

	return (
		<FormProvider {...form}>
			<Flex gap="1rem" direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap="1rem">
					<TextInput
						name="firstName"
						data-testid={ORGANIZATION_ELEMENT_ID.INVITE_MEMBER_FIRST_NAME_INPUT}
						control={control}
						size="md"
						label={tAdminDashboard('invite_member_drawer.form.first_name_field.label')}
						placeholder={tAdminDashboard(
							'invite_member_drawer.form.first_name_field.placeholder'
						)}
					/>
					<TextInput
						name="lastName"
						data-testid={ORGANIZATION_ELEMENT_ID.INVITE_MEMBER_LAST_NAME_INPUT}
						control={control}
						size="md"
						label={tAdminDashboard('invite_member_drawer.form.last_name_field.label')}
						placeholder={tAdminDashboard(
							'invite_member_drawer.form.last_name_field.placeholder'
						)}
					/>
					<TextInput
						name="email"
						data-testid={ORGANIZATION_ELEMENT_ID.INVITE_MEMBER_EMAIL_INPUT}
						control={control}
						size="md"
						label={tAdminDashboard('invite_member_drawer.form.email_field.label')}
						placeholder={tAdminDashboard(
							'invite_member_drawer.form.email_field.placeholder'
						)}
					/>
					<Select
						name="role"
						control={control}
						data={permissions}
						label={roleLabel}
						placeholder={tAdminDashboard(
							'invite_member_drawer.form.role_field.placeholder'
						)}
						size="md"
					/>
				</Flex>
				<Flex direction="column" gap="1rem">
					<Button
						fullWidth
						variant="primary"
						data-testid={ORGANIZATION_ELEMENT_ID.INVITE_MEMBER_SEND_BUTTON}
						rightSection={
							submitLabel ? undefined : <Icon variant="send-v3" size="vlg" />
						}
						mt="auto"
						disabled={!form.formState.isValid}
						onClick={() => {
							handleInviteMember(
								form.getValues('firstName'),
								form.getValues('lastName'),
								form.getValues('email'),
								form.getValues('role'),
								true
							);
						}}
					>
						{submitLabel ?? tAdminDashboard('invite_member_drawer.form.buttons.send')}
					</Button>

					<Button
						onClick={() =>
							handleInviteMember(
								form.getValues('firstName'),
								form.getValues('lastName'),
								form.getValues('email'),
								form.getValues('role'),
								false
							)
						}
						disabled={!form.formState.isValid}
						fullWidth
						variant="ghost"
					>
						{submitAndAddAnotherLabel ??
							tAdminDashboard(
								'invite_member_drawer.form.buttons.send_and_add_another'
							)}
					</Button>
				</Flex>
			</Flex>
		</FormProvider>
	);
};
